import React, { useContext, type ReactElement, useEffect, useMemo, useCallback } from 'react';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { SECTION_ITEM_APPS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_APPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { NavItemsContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import { mergeRefs } from '@atlassian/jira-merge-refs';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { ExploreMoreApps } from '../../../common/ui/explore-more-apps/ExploreMoreApps.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../common/constants.tsx';
import { createAttributes } from '../../../common/utils/get-attributes/index.tsx';
import { asyncNav4AppsContentViewQuery } from './async-content/asyncNav4AppsContentViewQuery.entrypoint.tsx';

type Nav4AppsDefaultProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	simulateLoading?: boolean; // For testing only
};

export const Nav4AppsDefault: React.FC<Nav4AppsDefaultProps> = componentWithFG(
	'navx-global-side-nav',
	Nav4AppsDefaultGSN,
	Nav4AppsDefaultNav4,
);

function Nav4AppsDefaultGSN(props: Nav4AppsDefaultProps): React.JSX.Element {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		simulateLoading = false,
		isExpanded = simulateLoading,
		onExpandFlyoutToggle,
		visualContentRef,
	} = props;
	const cloudId = useCloudId();

	let accountId: string | undefined;
	let activationId: string | undefined;
	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		const {
			data: {
				user: { accountId: accountIdFromUser },
			},
			// oxlint-disable-next-line react-hooks/rules-of-hooks
		} = useCurrentUser();

		// oxlint-disable-next-line react-hooks/rules-of-hooks
		activationId = useActivationId();
		accountId = accountIdFromUser || undefined;
	}
	const entryPointParams = useMemo(
		() => ({ cloudId, accountId, activationId }),
		[cloudId, accountId, activationId],
	);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncNav4AppsContentViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.APPS);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { moreMenuIds } = useContext(NavItemsContext);
	const getAttributes = useCallback(
		(error: Error) => createAttributes({ error, component: 'Nav4AppsDefault' }),
		[],
	);

	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, isExpanded]);

	const menuId = MENU_ID_APPS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();

				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				});
				const isInMore = moreMenuIds.includes(L1_MENU_ID.APPS);
				fireUIAnalytics(event, SECTION_ITEM_APPS, { isInMore });
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[createAnalyticsEvent, entryPointActions, moreMenuIds, onExpandFlyoutToggle],
	);

	const handleSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
			onSystemToggle={handleSystemToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-apps-default"
				isDragging={isDragging}
				ref={draggableRef ? mergeRefs(triggerRef, draggableRef) : triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{simulateLoading ? (
					<SkeletonNavMenuItems />
				) : (
					<JiraEntryPointContainer
						// Data stream, will subscribe to load updates.
						entryPointReferenceSubject={entryPointReferenceSubject}
						// Required props for error JSErrorBoundary.
						id="nav4-sidebar-apps-default"
						errorAttributes={getAttributes}
						packageName={PACKAGE_NAME}
						// Optional props for error JSErrorBoundary.
						errorFallback="flag"
						teamName={TEAM_NAME}
						// Fallback for `Suspense`.
						fallback={<SkeletonNavMenuItems />}
						// Props for the root component.
						runtimeProps={entryPointParams}
					/>
				)}
				<ExploreMoreApps />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4AppsDefaultNav4(props: Nav4AppsDefaultProps): React.JSX.Element {
	const { actionsOnHover, simulateLoading = false, isExpanded = simulateLoading } = props;
	const cloudId = useCloudId();
	let accountId: string | undefined;
	let activationId: string | undefined;
	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		const {
			data: {
				user: { accountId: accountIdFromUser },
			},
			// oxlint-disable-next-line react-hooks/rules-of-hooks
		} = useCurrentUser();

		// oxlint-disable-next-line react-hooks/rules-of-hooks
		activationId = useActivationId();
		accountId = accountIdFromUser || undefined;
	}
	const entryPointParams = useMemo(
		() => ({ cloudId, accountId, activationId }),
		[cloudId, accountId, activationId],
	);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncNav4AppsContentViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.APPS);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { moreMenuIds } = useContext(NavItemsContext);
	const getAttributes = useCallback(
		(error: Error) => createAttributes({ error, component: 'Nav4AppsDefault' }),
		[],
	);

	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, isExpanded]);

	const menuId = MENU_ID_APPS;

	const onSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();

				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				});
				const isInMore = moreMenuIds.includes(L1_MENU_ID.APPS);
				fireUIAnalytics(event, SECTION_ITEM_APPS, { isInMore });
			}
		},
		[createAnalyticsEvent, entryPointActions, moreMenuIds],
	);
	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onSystemToggle={onSystemToggle}
			onExpansionToggle={onExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-apps-default"
				actionsOnHover={actionsOnHover}
				// Prevent double-up of accessible name that occurs in icon+text situation.
				aria-label={formattedMessage}
				elemBefore={icon}
				ref={triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{simulateLoading ? (
					<SkeletonNavMenuItems />
				) : (
					<JiraEntryPointContainer
						// Data stream, will subscribe to load updates.
						entryPointReferenceSubject={entryPointReferenceSubject}
						// Required props for error JSErrorBoundary.
						id="nav4-sidebar-apps-default"
						errorAttributes={getAttributes}
						packageName={PACKAGE_NAME}
						// Optional props for error JSErrorBoundary.
						errorFallback="flag"
						teamName={TEAM_NAME}
						// Fallback for `Suspense`.
						fallback={<SkeletonNavMenuItems />}
						// Props for the root component.
						runtimeProps={entryPointParams}
					/>
				)}
				<ExploreMoreApps />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
